import React, { useEffect, useState } from 'react'
import { useFormik } from 'formik';
import * as Yup from "yup";
import LoadingOverlay from 'react-loading-overlay';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { useDispatch } from 'react-redux';
import { handlePostRequest } from '../../services/PostTemplate';
import { handlePatchRequest } from '../../services/PatchTemplate';
import { useHistory, useLocation } from "react-router-dom";
import { Triangle } from "react-loader-spinner";
import { baseURL } from '../../Config';
import { Image } from "primereact/image";
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { handleGetRequest } from '../../services/GetTemplate';

const AddEditMenu = () => {

    const location = useLocation()
    const { isEditable, rowData } = location?.state || {};
    const [saveBtnLoading, setSaveBtnLoading] = useState(false)
    const [isActive, setIsActive] = useState(false)
    const [selectedFiles, setSelectedFiles] = useState([])
    const [previewImage, setPreviewImage] = useState(null)
    const [allCategories, setAllCategories] = useState([])

    const history = useHistory()
    const dispatch = useDispatch()

    const validationSchema = Yup.object().shape({
        title: Yup.string().required("Menu title is required."),
        price: Yup.number().required("Price is required.").positive("Price must be positive."),
        categoryId: Yup.string().required("Category is required."),
        desc: Yup.string(),
        combo: Yup.boolean(),
        isAvailable: Yup.boolean(),
    });

    const formik = useFormik({
        validationSchema: validationSchema,
        initialValues: {
            title: "",
            desc: "",
            price: 0,
            combo: false,
            categoryId: "",
            isAvailable: true,
        },

        onSubmit: async (data) => {
            if (isEditable) {
                setSaveBtnLoading(true);
                setIsActive(true);

                let obj = {
                    title: formik.values.title,
                    desc: formik.values.desc || "",
                    price: formik.values.price,
                    combo: formik.values.combo,
                    categoryId: formik.values.categoryId,
                    isAvailable: formik.values.isAvailable,
                    imageBase64: selectedFiles[0]?.base64Data || null,
                }

                const response = await dispatch(handlePatchRequest(obj, `/menu/${rowData?._id}`, true, true));
                if (response?.status === 'success' || response?.statusCode === 200 || response?.statusCode === 201) {
                    history.push('./menus')
                    setSaveBtnLoading(false);
                    setIsActive(false);
                } else {
                    setSaveBtnLoading(false);
                    setIsActive(false);
                }
            } else {
                setSaveBtnLoading(true);
                setIsActive(true);

                let obj = {
                    title: formik.values.title,
                    desc: formik.values.desc || "",
                    price: formik.values.price,
                    combo: formik.values.combo,
                    categoryId: formik.values.categoryId,
                    isAvailable: formik.values.isAvailable !== undefined ? formik.values.isAvailable : true,
                    imageBase64: selectedFiles[0]?.base64Data || null,
                }

                const response = await dispatch(handlePostRequest(obj, "/menu/create", true, true));
                if (response?.status === 'success' || response?.statusCode === 200 || response?.statusCode === 201) {
                    history.push('./menus')
                    setSaveBtnLoading(false);
                    setIsActive(false);
                } else {
                    setSaveBtnLoading(false);
                    setIsActive(false);
                }
            }
        },
    });

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);
    const getFormErrorMessage = (name) => {
        return isFormFieldValid(name) && <small className="p-error">{formik.errors[name]}</small>;
    };

    // File Upload Handlers
    const handleIconClick = () => {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = 'image/*';
        fileInput.onchange = handleFileUpload;
        fileInput.click();
    };

    const handleFileUpload = (event) => {
        const files = Array.from(event.target.files || event.files);
        const updatedFiles = files.map((file) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64String = reader.result;
                setSelectedFiles([{ base64Data: base64String }]);
                setPreviewImage(base64String);
            };
            reader.readAsDataURL(file);
            return file;
        });
    };

    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
            handleFileUpload({ files: event.dataTransfer.files });
            event.dataTransfer.clearData();
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleRemoveImage = () => {
        setSelectedFiles([]);
        setPreviewImage(null);
    };

    //Get Category List
    const getCategoryList = async () => {
        const response = await dispatch(handleGetRequest(`/category/getAll`, false));
        if (response?.status === 'success' || response?.statusCode === 200) {
            setAllCategories(response?.data || []);
        }
    };

    //Get Menu By Id
    const getMenuById = async (rowData) => {
        setIsActive(true)
        if (rowData) {
            formik.setFieldValue('title', rowData?.title || '');
            formik.setFieldValue('desc', rowData?.desc || '');
            formik.setFieldValue('price', rowData?.price || 0);
            formik.setFieldValue('combo', rowData?.combo || false);
            formik.setFieldValue('isAvailable', rowData?.isAvailable !== undefined ? rowData.isAvailable : true);
            
            // Set category ID (handle both object and string)
            if (rowData?.categoryId) {
                const categoryId = typeof rowData.categoryId === 'object' ? rowData.categoryId._id : rowData.categoryId;
                formik.setFieldValue('categoryId', categoryId);
            }

            // Set preview image
            if (rowData?.image || rowData?.imageUrl) {
                const imagePath = rowData?.imageUrl ? rowData.imageUrl : rowData.image;
                setPreviewImage(`${baseURL}/${imagePath}`);
            }
        }
        setIsActive(false)
    }

    useEffect(() => {
        getCategoryList();
        if (isEditable && rowData) {
            getMenuById(rowData);
        }
    }, [isEditable, rowData])

    return (
        <>
            <LoadingOverlay
                active={isActive}
                spinner={<Triangle
                    height="120"
                    width="120"
                    color="#dc2626"
                    ariaLabel="triangle-loading"
                    wrapperStyle={{}}
                    wrapperClassName=""
                    visible={true}
                />}
                styles={{
                    overlay: (base) => ({
                        ...base,
                        background: 'rgb(38 41 51 / 78%)',
                    })
                }}
            >
                <div className="flex flex-column align-items-start md:flex-row  md:justify-content-between md:align-items-center mb-4">
                    <h4 className="mb-0" style={{ fontWeight: '600', letterSpacing: '0.5px', color: 'black' }}>
                        {isEditable ? 'Edit Menu Item' : 'Add New Menu Item'}
                    </h4>
                </div>

                <div className="card">
                    <form onSubmit={formik.handleSubmit}>
                        <div className="grid">
                            {/* Image Upload Section */}
                            <div className="col-12">
                                <div className="input-card flex flex-column p-3">
                                    <div className='flex-row'>
                                        <label>Menu Image</label>
                                    </div>
                                    <div className="grid">
                                        <div className="col-12 md:col-4">
                                            {previewImage ? (
                                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                                    <Image
                                                        src={previewImage}
                                                        alt="Menu"
                                                        width="200"
                                                        height="200"
                                                        preview
                                                        imageStyle={{ borderRadius: '8px', objectFit: 'cover' }}
                                                    />
                                                    <i
                                                        className="pi pi-times"
                                                        style={{
                                                            position: 'absolute',
                                                            top: '5px',
                                                            right: '5px',
                                                            cursor: 'pointer',
                                                            fontSize: '1.2em',
                                                            color: 'white',
                                                            padding: '8px',
                                                            background: 'rgba(220, 38, 38, 0.9)',
                                                            borderRadius: '50%'
                                                        }}
                                                        onClick={handleRemoveImage}
                                                    />
                                                </div>
                                            ) : (
                                                <div
                                                    className="flex align-items-center flex-column"
                                                    onDrop={handleDrop}
                                                    onDragOver={handleDragOver}
                                                    style={{
                                                        border: '2px dashed #dc2626',
                                                        padding: '40px',
                                                        width: '100%',
                                                        textAlign: 'center',
                                                        borderRadius: '8px',
                                                        cursor: 'pointer',
                                                        backgroundColor: '#fef2f2'
                                                    }}
                                                    onClick={handleIconClick}
                                                >
                                                    <i className="pi pi-cloud-upload" style={{ fontSize: '3em', color: '#dc2626', marginBottom: '10px' }}></i>
                                                    <span style={{ fontSize: '1.1em', color: '#dc2626', fontWeight: 'bold' }}>
                                                        Click to Upload
                                                    </span>
                                                    <span style={{ fontSize: '0.9em', color: '#666', marginTop: '5px' }}>
                                                        JPG or PNG (Recommended: 500x500px)
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Title */}
                            <div className="col-12 md:col-6">
                                <div className="input-card flex flex-column p-3">
                                    <div className='flex-row'>
                                        <label>Menu Title</label>
                                        <span className="Label__Required">*</span>
                                    </div>
                                    <InputText
                                        className="p-inputtext-sm"
                                        id="title"
                                        name="title"
                                        value={formik.values.title}
                                        onChange={formik.handleChange}
                                        placeholder="Enter menu title"
                                    />
                                    <span className='ml-2'>
                                        {getFormErrorMessage('title')}
                                    </span>
                                </div>
                            </div>

                            {/* Category */}
                            <div className="col-12 md:col-6">
                                <div className="input-card flex flex-column p-3">
                                    <div className='flex-row'>
                                        <label>Category</label>
                                        <span className="Label__Required">*</span>
                                    </div>
                                    <Dropdown
                                        value={formik.values.categoryId}
                                        options={allCategories}
                                        onChange={(e) => formik.setFieldValue('categoryId', e.value)}
                                        optionLabel="name"
                                        optionValue="_id"
                                        placeholder="Select Category"
                                        className={isFormFieldValid('categoryId') ? 'p-invalid' : ''}
                                    />
                                    <span className='ml-2'>
                                        {getFormErrorMessage('categoryId')}
                                    </span>
                                </div>
                            </div>

                            {/* Price */}
                            <div className="col-12 md:col-6">
                                <div className="input-card flex flex-column p-3">
                                    <div className='flex-row'>
                                        <label>Price ($)</label>
                                        <span className="Label__Required">*</span>
                                    </div>
                                    <InputNumber
                                        id="price"
                                        name="price"
                                        value={formik.values.price}
                                        onValueChange={(e) => formik.setFieldValue('price', e.value)}
                                        mode="decimal"
                                        min={0}
                                        placeholder="0.00"
                                        className={isFormFieldValid('price') ? 'p-invalid' : ''}
                                        style={{ width: '100%' }}
                                    />
                                    <span className='ml-2'>
                                        {getFormErrorMessage('price')}
                                    </span>
                                </div>
                            </div>

                            {/* Combo */}
                            <div className="col-12 md:col-6">
                                <div className="input-card flex flex-column p-3">
                                    <div className='flex-row'>
                                        <label>Is Combo?</label>
                                    </div>
                                    <div className="flex align-items-center">
                                        <InputSwitch
                                            checked={formik.values.combo}
                                            onChange={(e) => formik.setFieldValue('combo', e.value)}
                                        />
                                        <span className="ml-2">{formik.values.combo ? 'Yes' : 'No'}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Available */}
                            <div className="col-12 md:col-6">
                                <div className="input-card flex flex-column p-3">
                                    <div className='flex-row'>
                                        <label>Available</label>
                                    </div>
                                    <div className="flex align-items-center">
                                        <InputSwitch
                                            checked={formik.values.isAvailable}
                                            onChange={(e) => formik.setFieldValue('isAvailable', e.value)}
                                        />
                                        <span className="ml-2">{formik.values.isAvailable ? 'Available' : 'Not Available'}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Description */}
                            <div className="col-12">
                                <div className="input-card flex flex-column p-3">
                                    <div className='flex-row'>
                                        <label>Description</label>
                                    </div>
                                    <InputTextarea
                                        id="desc"
                                        name="desc"
                                        value={formik.values.desc}
                                        onChange={formik.handleChange}
                                        rows={4}
                                        placeholder="Enter menu description"
                                        className="w-full"
                                    />
                                    <span className='ml-2'>
                                        {getFormErrorMessage('desc')}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-content-end mt-4">
                            <Button
                                type="button"
                                label="Cancel"
                                icon="pi pi-times"
                                className="p-button-text mr-2"
                                onClick={() => history.push('./menus')}
                            />
                            <Button
                                type="submit"
                                label={isEditable ? "Update Menu" : "Create Menu"}
                                icon="pi pi-check"
                                loading={saveBtnLoading}
                                className="Add__New-Button"
                            />
                        </div>
                    </form>
                </div>
            </LoadingOverlay>
        </>
    );
};

export default AddEditMenu;

